import React, { useState, useRef, useEffect } from 'react';
import { Video, ClipboardList, Clock, FileText, AlertTriangle, Circle, Square, CheckCircle, RefreshCw, Lightbulb } from 'lucide-react';
import '../../styles/wizard-steps.css';

interface VideoRecordingStepProps {
  consentText: string;
  onNext: (videoBlob: Blob, duration: number) => void;
  onBack: () => void;
  minimumDuration?: number; // in seconds
}

const VideoRecordingStep: React.FC<VideoRecordingStepProps> = ({
  consentText,
  onNext,
  onBack,
  minimumDuration = 10
}) => {
  const [isRecording, setIsRecording] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [duration, setDuration] = useState(0);
  const [finalDuration, setFinalDuration] = useState(0);
  const [error, setError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraReady(true);
      setError('');
    } catch (err) {
      setError('Could not access camera. Please grant camera permissions.');
      console.error('Camera error:', err);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;

    chunksRef.current = [];
    const mediaRecorder = new MediaRecorder(streamRef.current, {
      mimeType: 'video/webm;codecs=vp9'
    });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setRecordedBlob(blob);
      
      // Calculate final duration from actual elapsed time
      const actualDuration = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setFinalDuration(actualDuration);
      setDuration(actualDuration);
      
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Show playback
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = URL.createObjectURL(blob);
      }
    };

    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setIsRecording(true);
    setDuration(0);
    startTimeRef.current = Date.now();

    // Start timer
    timerRef.current = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const retakeVideo = () => {
    setRecordedBlob(null);
    setDuration(0);
    setFinalDuration(0);
    setError('');
    startCamera();
  };

  const handleContinue = () => {
    const actualDuration = finalDuration || duration;
    if (recordedBlob && actualDuration >= minimumDuration) {
      onNext(recordedBlob, actualDuration);
    } else {
      setError(`Recording must be at least ${minimumDuration} seconds`);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const actualDuration = finalDuration || duration;
  const canContinue = recordedBlob !== null && actualDuration >= minimumDuration;

  return (
    <div className="wizard-step video-step">
      <div className="step-header">
        <div className="step-icon"><Video size={24} /></div>
        <h2 className="step-title">Video Consent</h2>
        <p className="step-subtitle">Record your verbal consent</p>
      </div>

      <div className="video-instructions">
        <h3><ClipboardList size={20} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Instructions:</h3>
        <ol>
          <li>Point camera at the person providing consent</li>
          <li>Say "I Agree" while looking at camera before submitting</li>
        </ol>
        <p className="requirement-text">
          <Clock size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Minimum recording duration: {minimumDuration} seconds
        </p>
      </div>

      <div className="consent-text-box">
        <h4><FileText size={20} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Please read this statement:</h4>
        <div className="consent-text-content">
          "{consentText}"
        </div>
      </div>

      <div className="video-container">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isRecording}
          className={`video-preview ${recordedBlob ? 'playback' : ''}`}
          controls={recordedBlob !== null}
        />
        
        {isRecording && (
          <div className="recording-indicator">
            <span className="recording-dot"></span>
            <span>Recording: {formatDuration(duration)}</span>
          </div>
        )}

        {!cameraReady && !error && (
          <div className="camera-loading">
            <p>Loading camera...</p>
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">
          <AlertTriangle size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> {error}
        </div>
      )}

      <div className="video-controls">
        {!recordedBlob ? (
          <>
            {!isRecording ? (
              <button
                onClick={startRecording}
                disabled={!cameraReady}
                className="btn btn-danger"
              >
                <Circle size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Start Recording
              </button>
            ) : (
              <button
                onClick={stopRecording}
                className="btn btn-secondary"
              >
                <Square size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Stop Recording
              </button>
            )}
          </>
        ) : (
          <div className="recorded-controls">
            <p className="success-text">
              <CheckCircle size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Recorded: {formatDuration(actualDuration)}
            </p>
            <button onClick={retakeVideo} className="btn btn-secondary">
              <RefreshCw size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Retake
            </button>
          </div>
        )}
      </div>

      <div className="step-actions">
        <button className="btn-link" onClick={onBack} disabled={isRecording}>
          ← Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className="btn btn-primary"
        >
          {canContinue ? 'Continue →' : 'Recording Required'}
        </button>
      </div>

      <div className="help-text">
        <p><Lightbulb size={16} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }} /> Make sure you are in a quiet place with good lighting</p>
      </div>
    </div>
  );
};

export default VideoRecordingStep;
